import { type FrameValue, useSpring } from '@react-spring/web'
import { useState } from 'react'

import { type ValidationErrors } from './validation/validation-messages-hook'

export interface StepPreviewAnimation {
  startBlinking: () => void
  artworkSizeOpacity: FrameValue<number> | number
  artworkImageOpacity: FrameValue<number> | number
  frameStyleOpacity: FrameValue<number> | number
  passepartoutOpacity: FrameValue<number> | number
  glassOpacity: FrameValue<number> | number
  extrasOpacity: FrameValue<number> | number
}

const TIMING_PRECISION = 1000
const BLINK_DURATION = 400
const BLINK_COUNT = 3

export const defaultStepPreviewAnimation = {
  startBlinking: () => null,
  artworkSizeOpacity: 1,
  artworkImageOpacity: 1,
  frameStyleOpacity: 1,
  passepartoutOpacity: 1,
  glassOpacity: 1,
  extrasOpacity: 1,
}

/**
 * Returns opacity value and blinking state setter.
 */
export const useBlinkingState = () => {
  const [isBlinking, setIsBlinking] = useState(false)

  const { blinkingProgress } = useSpring({
    from: {
      blinkingProgress: 0,
    },
    blinkingProgress: isBlinking ? 1 : 0,
    config: {
      duration: BLINK_DURATION * BLINK_COUNT,
    },
    reset: true,
    onRest: () => {
      setIsBlinking(false)
    },
  })

  // Calculate opacity value based on total time elapsed since start
  const opacity = blinkingProgress.to((value) =>
    Math.abs(
      (Math.round(value * BLINK_COUNT * 2 * TIMING_PRECISION) %
        (2 * TIMING_PRECISION)) /
        TIMING_PRECISION -
        1
    )
  )

  return [opacity, setIsBlinking] as const
}

/**
 * Manages step preview blinking and opacity values.
 */
export const useStepPreviewAnimation = (
  validationMessages: ValidationErrors
): StepPreviewAnimation => {
  const [artworkSizeOpacity, setIsArtworkSizeBlinking] = useBlinkingState()
  const [frameStyleOpacity, setIsFrameStyleBlinking] = useBlinkingState()
  const [passepartoutOpacity, setIsPassepartoutBlinking] = useBlinkingState()
  const [glassOpacity, setIsGlassBlinking] = useBlinkingState()

  // TODO: Wrong way to check for valid state!
  const startBlinking = () => {
    // Start blinking animation for invalid step previews
    setIsArtworkSizeBlinking(!!validationMessages?.artowkSizePreview)
    setIsFrameStyleBlinking(
      !!validationMessages?.frameStyleProfilePreview ||
        !!validationMessages?.frameStyleTexturePreview
    )
    setIsPassepartoutBlinking(
      !!validationMessages?.passepartoutSizePreview ||
        !!validationMessages?.passepartoutColorPreview
    )
    setIsGlassBlinking(!!validationMessages?.glassTypePreview)
  }

  return {
    startBlinking,
    artworkSizeOpacity,
    artworkImageOpacity: 1,
    frameStyleOpacity,
    passepartoutOpacity,
    glassOpacity,
    extrasOpacity: 1,
  }
}
